import React, { useState } from "react";
import { AxiosError } from "axios";
import { Button } from "@mui/material";
import { Box } from "@mui/system";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Dialog, Label16Regular, TextField, COLORS } from "components";
import { addEmissionsAllocationDimensions, AddEmissionsAllocationDimensionsRequestBody, EmissionsAllocationDetails } from "api";
import { useNotify } from "hooks";
import { parseStringBySeparator } from "utils";
import { PlusIconBlue } from "assets";

export const AddNewColumnButton = ({ allocationId }: { allocationId: number }) => {
    const [titles, setTitles] = useState("");
    const [addNewColumnModalOpen, setAddNewColumnModalOpen] = useState(false);
    const notify = useNotify();
    const queryClient = useQueryClient();

    const { mutateAsync: addNewColumnMutation, isLoading: isSaving } = useMutation<
        EmissionsAllocationDetails,
        AxiosError,
        AddEmissionsAllocationDimensionsRequestBody
    >({
        mutationFn: addEmissionsAllocationDimensions,
        onSuccess: () => {
            notify.success("New column(s) added successfully.");
            queryClient.invalidateQueries(["emissionsAllocationDetails", allocationId]);
            setAddNewColumnModalOpen(false);
        },
        onError: (error: AxiosError) => {
            notify.error(typeof error.response?.data === 'string' ? error.response?.data : "Failed to add new column(s).");
        },
    });

    const handleSave = () => {
        const titleArray = parseStringBySeparator(titles, ';');
        if (titleArray.length === 0) {
            notify.error("Invalid input. Please ensure adding a semi-colon between each title.");
            return;
        }
        addNewColumnMutation({ allocationId, dimensions: titleArray });
    };

    const handleCancel = () => {
        setAddNewColumnModalOpen(false);;
    };

    const onAddColumnClick = () => {
        setAddNewColumnModalOpen(true);
    };

    return (<>
        <Button
            variant="outlined"
            startIcon={<PlusIconBlue />}
            sx={{ borderRadius: "8px", width: '140px' }}
            onClick={onAddColumnClick}
        >
            Add Column
        </Button>
        {addNewColumnModalOpen && <Dialog isOpen={addNewColumnModalOpen} title={<div style={{ color: COLORS.Mainblue, fontSize: '16px' }}>Add New Column</div>} dividers onClose={handleCancel}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                <div style={{ color: COLORS.Mainblue, fontSize: '20px', fontWeight: '500' }}>Dimension Column Titles</div>
                <div>
                    <Label16Regular>Copy and paste your titles from Excel OR</Label16Regular>
                    <Label16Regular>manually type your data - adding a semi-colon between each title.</Label16Regular>
                </div>
                <TextField
                    name="dimensionColumnTitles"
                    value={titles}
                    onChange={(e) => setTitles(e.target.value)}
                    fullWidth
                    multiline
                    rows={4}
                    variant="outlined"
                    sx={{
                        width: "100%",
                        "& .MuiInputBase-root textarea": {
                            resize: "vertical",
                            overflow: "auto",
                        },
                    }}
                />
                <Box sx={{ display: "flex", justifyContent: "flex-end", gap: "10px", mt: "20px" }}>
                    <Button variant="outlined" onClick={handleCancel} disabled={isSaving}>
                        Cancel
                    </Button>
                    <Button variant="contained" color="primary" onClick={handleSave} disabled={isSaving}>
                        Save
                    </Button>
                </Box>
            </Box>
        </Dialog>}</>
    );
};